/**
 * SAFER-i™ pricing data — NEW SGD structure (Mar 2026).
 *
 * Single source of truth: backend /api/pricing. This file is the fallback
 * used during initial render and shared helpers (color classes, etc).
 */
import { Briefcase, Building } from 'lucide-react';
import {
  FREELANCER_PLAN, ORGANIZATION_PLAN, UNIT_PURCHASE,
  ROLE_MONTHLY_UNITS, ROLE_LABELS, TRIAL_DAYS,
} from '@/config/tierPricing';

/**
 * The two plans the platform now offers. Tier selection is gone — users
 * pick Freelancer (SGD 3.99/mo) or Organization (SGD 499/yr) and that's it.
 * Unit packs are purchased separately from /billing.
 */
export const PLAN_OPTIONS = {
  freelancer: {
    id: 'freelancer',
    name: FREELANCER_PLAN.name,
    priceSgd: FREELANCER_PLAN.priceSgd,
    priceUsd: FREELANCER_PLAN.priceUsd,
    priceDisplay: `SGD $${FREELANCER_PLAN.priceSgd.toFixed(2)}`,
    secondaryDisplay: `~ USD $${FREELANCER_PLAN.priceUsd.toFixed(2)}`,
    period: '/month',
    tagline: 'For independent consultants',
    description: FREELANCER_PLAN.description,
    color: 'emerald',
    icon: Briefcase,
    features: FREELANCER_PLAN.features,
    popular: false,
  },
  organization: {
    id: 'organization',
    name: ORGANIZATION_PLAN.name,
    priceSgd: ORGANIZATION_PLAN.onboardingSgd,
    priceUsd: ORGANIZATION_PLAN.onboardingUsd,
    priceDisplay: `SGD $${ORGANIZATION_PLAN.onboardingSgd.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    secondaryDisplay: `~ USD $${ORGANIZATION_PLAN.onboardingUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    period: '/year',
    tagline: 'For teams of any size',
    description: ORGANIZATION_PLAN.description,
    color: 'indigo',
    icon: Building,
    features: ORGANIZATION_PLAN.features,
    popular: true,
  },
};

export const PLAN_ORDER = ['freelancer', 'organization'];

/** Unit pack pricing snapshot for the post-signup unit purchase flow. */
export const UNIT_PACKS = {
  initial: {
    units: UNIT_PURCHASE.minInitialUnits,
    sgd: UNIT_PURCHASE.minInitialCostSgd,
    usd: UNIT_PURCHASE.minInitialCostUsd,
    label: `Initial pack — ${UNIT_PURCHASE.minInitialUnits.toLocaleString()} units (required)`,
    note: `Minimum first-time purchase at SGD $${UNIT_PURCHASE.unitPriceSgd}/unit.`,
  },
  addon: {
    units: UNIT_PURCHASE.addonBlockSize,
    sgd: UNIT_PURCHASE.addonBlockCostSgd,
    usd: UNIT_PURCHASE.addonBlockCostUsd,
    label: `Add-on block — ${UNIT_PURCHASE.addonBlockSize} units`,
    note: 'Top up in 100-unit blocks anytime.',
  },
};

/** Role consumption matrix — what each role costs the org per month. */
export const ROLE_CONSUMPTION = Object.entries(ROLE_MONTHLY_UNITS)
  .filter(([code]) => code !== 'ADMIN')  // ADMIN === HQ rate; show once
  .map(([code, units]) => ({
    code,
    label: ROLE_LABELS[code] || code,
    monthlyUnits: units,
  }));

export const TRIAL_DAYS_DISPLAY = TRIAL_DAYS;

/** Ala-carte services — sourced from backend at runtime, this is the fallback. */
export const ADD_ON_SERVICES = [
  {
    name: 'Executive / Leadership Coaching',
    priceDisplay: 'SGD 997',
    unit: '/ 3-hour coaching block',
    description: 'One-on-one strategy and drift-diagnosis with a SAFER-i™ certified coach.',
  },
  {
    name: 'Coaching Bundle (6 blocks)',
    priceDisplay: 'SGD 5,982',
    unit: '/ bundle',
    description: 'Six 3-hour coaching blocks for teams running a full quarterly intervention cycle.',
  },
  {
    name: 'The Moment Just Before©️ Workshop',
    priceDisplay: 'SGD 19,997',
    unit: '/ 1-day workshop',
    note: 'Up to 25 participants',
    description: '1-Day High-Impact Leadership & Safety Intervention.',
  },
];

/** Color utility — kept compatible with the old API for components that still import it. */
export function getColorClasses(color) {
  const colors = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-500', text: 'text-emerald-600', badge: 'bg-emerald-500', icon: 'bg-emerald-100 text-emerald-600', button: 'bg-emerald-600 hover:bg-emerald-700' },
    indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-500',  text: 'text-indigo-600',  badge: 'bg-indigo-500',  icon: 'bg-indigo-100 text-indigo-600',  button: 'bg-indigo-600 hover:bg-indigo-700' },
    // Legacy palette kept for components that still pass green/blue/purple/amber
    green:  { bg: 'bg-emerald-50', border: 'border-emerald-500', text: 'text-emerald-600', badge: 'bg-emerald-500', icon: 'bg-emerald-100 text-emerald-600', button: 'bg-emerald-600 hover:bg-emerald-700' },
    blue:   { bg: 'bg-indigo-50',  border: 'border-indigo-500',  text: 'text-indigo-600',  badge: 'bg-indigo-500',  icon: 'bg-indigo-100 text-indigo-600',  button: 'bg-indigo-600 hover:bg-indigo-700' },
    purple: { bg: 'bg-violet-50',  border: 'border-violet-500',  text: 'text-violet-600',  badge: 'bg-violet-500',  icon: 'bg-violet-100 text-violet-600',  button: 'bg-violet-600 hover:bg-violet-700' },
    amber:  { bg: 'bg-amber-50',   border: 'border-amber-500',   text: 'text-amber-600',   badge: 'bg-amber-500',   icon: 'bg-amber-100 text-amber-600',   button: 'bg-amber-600 hover:bg-amber-700' },
  };
  return colors[color] || colors.indigo;
}
